import styled, { css } from 'styled-components'
import Paragraph from '../typography/paragraph'
import typography, { ParagraphTypes } from '../../theme/basic/typography'

export interface Props {
  hasError?: boolean
  isDisabled?: boolean
  isActive?: boolean
  isBold?: boolean
}

export interface ChevronProps {
  isDisabled?: boolean
  isOpen?: boolean
}

export interface DropdownProps {
  isDisabled?: boolean
  isScrollable?: boolean
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const sharedFormStyle = () => css<Props>`
  ${({ theme }) => theme.TYPOGRAPHY[ParagraphTypes.PARAGRAPH_1]};
  color: ${({ theme }) => theme.COLOR.PRIMARY_BLACK};
  height: 64px;
  padding: 16px 24px;
  border-radius: 8px;
  border: none;
  line-height: 4.4rem;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  background-color: ${({ theme }) => theme.COLOR.NEUTRAL_GREY_3};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.COLOR.PRIMARY_LIGHT_GREY};

  &::placeholder {
    color: ${({ theme }) => theme.COLOR.NEUTRAL_GREY_4};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.COLOR.NEUTRAL_GREY_3};
    color: ${({ theme }) => theme.COLOR.NEUTRAL_GREY_4};

    &::placeholder {
      color: ${({ theme }) => theme.COLOR.NEUTRAL_GREY_3};
    }
  }

  &:focus {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.COLOR.SECONDARY_BLUE};
  }
};
};

  ${({ isBold }) =>
    (isBold ?? false) &&
    css`
      font-weight: 600;

      &::placeholder {
        font-weight: 600;
      }
    `};

  ${({ hasError }) =>
    (hasError ?? false) &&
    css`
      border-color: ${({ theme }) => theme.COLOR.SUPPORT_INVALID};
      color: ${({ theme }) => theme.COLOR.SUPPORT_INVALID};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.COLOR.SUPPORT_INVALID};

      &:focus {
        box-shadow: 0 0 0 1px ${({ theme }) => theme.COLOR.SUPPORT_INVALID};
      }
    `};
`

export const StyledFieldContainer = styled.div`
  position: relative;
  width: 100%;
`

export const StyledField = styled.input<Props>`
  ${sharedFormStyle};
`

export const StyledTextAreaField = styled.textarea<Props>`
  ${sharedFormStyle};
  height: auto;
  line-height: 2.6rem;
  padding: 1.2rem;
  width: 100%;
`

export const StyledSelect = styled.select<Props>`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  cursor: pointer;
  ${sharedFormStyle}

  &:invalid {
    color: ${({ theme }) => theme.COLOR.NEUTRAL_GREY_3};
  }

  ${({ isActive }) =>
    (isActive ?? false) &&
    css`
      box-shadow: 0 0 0 1px ${({ theme }) => theme.COLOR.PRIMARY_BLACK};
    `}
`

export const StyledLabel = styled.div<Props>`
  ${typography[ParagraphTypes.PARAGRAPH_HIGHLIGHT]};
  display: block;

  margin-bottom: 6px;
  font-family: Gilroy, sans-serif;
  font-size: 24px;
  line-height: 32px;
  font-weight: 800;

  ${({ isDisabled, theme }) =>
    (isDisabled ?? false) &&
    css`
      color: ${theme.COLOR.NEUTRAL_GREY_4};
    `}

    @media (max-width: 768px) {
    font-size: 18px;
    line-height: 1.3;
    }
    
`

export const StyledError = styled(Paragraph)(
  () => css`
    margin-top: 0.8rem;
    color: ${({ theme }) => theme.COLOR.SUPPORT_INVALID};
  `
)

export const StyledRequired = styled.span(
  () => css`
  margin-right: 5px;
    color: ${({ theme }) => theme.COLOR.SUPPORT_INVALID};
  `
)

export const StyledOuterLabel = styled.label`
  width: 100%;
`

export const StyledDescription = styled.span`
  display: block;
  color: #565656;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.6;
  margin: 10px 0;
`

export const StyledDropdown = styled.div`
  position: relative;
`

export const StyledDropdownMenu = styled.div<DropdownProps>`
  position: absolute;
  top: 1rem;
  // left: 0;
  // right: 0;
  background: ${({ theme }) => theme.COLOR.PRIMARY_WHITE};
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
  z-index: 2;
  opacity: 1;
  transition: opacity 0.2s ease;

  &::before {
    display: block;
    width: 2rem;
    height: 2rem;
    content: '';
    position: absolute;
    left: 50%;
    top: -1rem;
    background: ${({ theme }) => theme.COLOR.PRIMARY_WHITE};
    box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
    transform: translate(-50%, 0) rotate(45deg);
  }

  ${({ isDisabled }) =>
    (isDisabled ?? false) &&
    css`
      opacity: 0;
      pointer-events: none;
    `};
`

export const DropDownMenuContent = styled.div<DropdownProps>`
  position: relative;
  z-index: 2;
  width: 380px;
  background: ${({ theme }) => theme.COLOR.PRIMARY_WHITE};

  ${({ isScrollable }) =>
    (isScrollable ?? false) &&
    css`
      overflow-y: scroll;
      max-height: 14rem;
    `};
`

export const StyledDropdownOption = styled.div`
  cursor: pointer;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.COLOR.PRIMARY_BLACK};
  height: 3.2rem;
  line-height: 3.2rem;
  padding-left: 6px;

  &:hover {
    color: ${({ theme }) => theme.COLOR.PRIMARY_WHITE};
    background: ${({ theme }) => theme.COLOR.PRIMARY_BLACK};
    font-weight: 600;
  }
`

export const FormFieldContainer = styled.div`
  margin-bottom: 4rem;
`

export const FormFields2ColContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
  grid-template-columns: 1fr;
}
`

export const RadioButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  margin-top: 20px;
  @media (max-width: 768px) {
    margin-top: 1rem;
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`
